/**
 * ServerEntranceShrines (`q-dungeon-server-entrance-has-no-shrine`) — draws the
 * shrine at every entrance the server advertises (`helloAck.world.dungeons`),
 * for every client, flag or no flag. Before this, the only sign of a real
 * dungeon was the 'Press G' prompt inside its reach (`runClient.ts`).
 *
 * Presentation only: no input, no wire, no lifecycle. Entry stays the run
 * client's G against the server's reach. The site, profile and scene plan come
 * from the pure `serverEntranceShrine` (run.ts); this class only renders them
 * with the techdemo's `ShrineVisual`. Seated per AD-408 by the caller (Game.ts
 * `seatDungeonEntrances`), and tagged world content so a run hides it (AD-705).
 */

import * as THREE from 'three';

import type { DungeonSiteWire } from '@great-cloud/shared/client';
import type { DungeonEntranceAnchor, DungeonShrineProfile } from '@great-cloud/procedural-dungeons';

import { markWorldContent } from '../scene/worldContent.js';
import { serverEntranceShrine } from './run.js';
import { ShrineVisual, type ShrineModelSource } from './shrineRenderer.js';
import bundled from './catalog.json' with { type: 'json' };

const BUNDLE = bundled as unknown as {
  anchors: DungeonEntranceAnchor[];
  shrineProfiles: DungeonShrineProfile[];
};

export class ServerEntranceShrines {
  readonly group = new THREE.Group();
  private readonly visuals: ShrineVisual[] = [];

  constructor(
    private readonly scene: THREE.Scene,
    sites: readonly DungeonSiteWire[],
    zoneId: string,
    warn: (message: string) => void,
    loadModel: ShrineModelSource | null = null,
  ) {
    this.group.name = 'dungeon-server-entrances';
    scene.add(markWorldContent(this.group));
    for (const wire of sites) {
      const shrine = serverEntranceShrine(wire, zoneId, BUNDLE.anchors, BUNDLE.shrineProfiles);
      if (!shrine) {
        warn(`no shrine for entrance ${wire.id} (difficulty ${wire.difficulty})`);
        continue;
      }
      const visual = new ShrineVisual(shrine.site, shrine.profile, loadModel);
      visual.rebuild(shrine.plan);
      this.visuals.push(visual);
      this.group.add(visual.group);
    }
  }

  /** Re-asks every shrine for its approved GLB (the content pack declaring the kit just loaded). */
  requestModels(source: ShrineModelSource): void {
    for (const visual of this.visuals) visual.requestModel(source);
  }

  get count(): number {
    return this.visuals.length;
  }

  /** Per-frame glow pulse and ring fade against the local player. */
  frame(nowMs: number, playerX: number, playerZ: number): void {
    for (const visual of this.visuals) visual.animate(nowMs, playerX, playerZ);
  }

  dispose(): void {
    for (const visual of this.visuals) visual.dispose();
    this.visuals.length = 0;
    this.scene.remove(this.group);
  }
}
